import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import {
  batchesKey,
  changeKey,
  decideBatch,
  getBatches,
  getChange,
  LandingPagesError,
  recoverVariant,
  retireVariant,
  writeMessage,
  type Batch,
  type BatchResult,
  type PageSummary,
  type RecoverVariantResult,
  type RetireVariantResult,
} from "./api";
import {
  batchConfirmLabel,
  batchEffectLine,
  batchDeployState,
  batchTitle,
  changeLine,
  formatDay,
  languageName,
  personName,
  recoverableVariants,
  recoverVariantPlan,
  retirableVariants,
  retireVariantPlan,
} from "./format";
import { approvalView, PreviewLink, useChangePoll, useMe, useRefreshLanding } from "./WriteActions";

// Bulk mode (transparency specs/2026-09-29-landing-console-bulk.md): one decision per variant
// across every language of a funnel. The Worker enforces every rule; these controls only show them.

const names = (locales: string[]) => locales.map(languageName).join(", ");

type Outcome = { decision: "approve" | "reject"; results: BatchResult[] };

/**
 * Every pending batch in this funnel, each with Approve all / Reject all. A decided batch keeps its
 * result here: once decided it leaves the pending list, and the result must not leave with it.
 */
export function PendingBatches({ funnelSlug }: { funnelSlug: string }) {
  const [decided, setDecided] = useState<Record<string, { batch: Batch; outcome: Outcome }>>({});
  const batches = useQuery({
    queryKey: batchesKey(funnelSlug),
    queryFn: () => getBatches(funnelSlug),
    refetchInterval: (query) =>
      query.state.data?.data.batches.some((b) => b.items.some((i) => i.preview?.status === "building")) ? 15_000 : false,
  });
  // An older Worker has no batch route; the per-page panels still work, so show nothing.
  // A fully decided batch leaves the list; one with failures stays so what is left can be retried.
  const pending = (batches.data?.data.batches ?? []).filter(
    (b) => !decided[b.batch_id]?.outcome.results.every((r) => r.ok),
  );
  const done = Object.values(decided);
  if (pending.length === 0 && done.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      {done.map(({ batch, outcome }) => (
        <DecidedPanel key={`decided-${batch.batch_id}`} batch={batch} outcome={outcome} />
      ))}
      {pending.map((batch) => (
        <BatchPanel
          key={batch.batch_id}
          batch={batch}
          funnelSlug={funnelSlug}
          onDecided={(outcome) => setDecided((prev) => ({ ...prev, [batch.batch_id]: { batch, outcome } }))}
        />
      ))}
    </div>
  );
}

/** The result of Approve all / Reject all, following the deploy when anything merged. */
function DecidedPanel({ batch, outcome }: { batch: Batch; outcome: Outcome }) {
  const client = useQueryClient();
  const poll = useChangePoll(null);
  // Follow the deploy of the last merge: every merge before it is in that build.
  const merged = outcome.decision === "approve" ? outcome.results.filter((r) => r.ok && r.merge_sha) : [];
  const last = merged[merged.length - 1];
  const deploy = useQuery({
    queryKey: changeKey(last?.folder ?? "", last?.merge_sha),
    queryFn: () => getChange(last!.folder, last!.merge_sha),
    enabled: Boolean(last),
    // The same polling as a page's panel (changePollMs), each slow state for a while only.
    refetchInterval: (query) => {
      const data = query.state.data?.data;
      return data?.deploy ? poll(data) : 10_000;
    },
  });
  const deployState = deploy.data?.data.deploy ?? null;
  const completed = deployState?.status === "completed";
  // The same reading as a page's panel: deploying, failed, staging, promoting (the finisher's run), a failed promotion, or live.
  const view = approvalView(deploy.data?.data);
  // Reload the page and list views (not the change query, which holds the deploy state) once the deploy ends either
  // way (merged pages are no longer pending even if it failed), and once more when the release goes live, as a page's
  // panel does.
  const live = view?.state === "done";
  useEffect(() => {
    if (completed) {
      void client.invalidateQueries({
        predicate: (query) => query.queryKey[0] === "landing_pages" && query.queryKey[1] !== "change",
      });
    }
  }, [completed, live, client]);

  const failed = outcome.results.filter((r) => !r.ok);
  const count = `${outcome.results.length - failed.length} of ${outcome.results.length}`;
  const verb = outcome.decision === "approve" ? "Approved" : "Rejected";
  const viewState = view?.state;
  // Before the merge's deploy is read (and for the pre-merge states, which a merged change never has) it is updating.
  const state = !last ? "" : batchDeployState(batch.kind, !viewState || viewState === "checking" || viewState === "checks_failed" ? "deploying" : viewState);
  // Approved but still waiting for their checks (bug 3fd0402f): each publishes when its checks pass.
  const waiting = outcome.decision === "approve" ? outcome.results.filter((r) => r.ok && r.state === "waiting_checks") : [];
  const checksFailed = outcome.decision === "approve" ? outcome.results.filter((r) => r.ok && r.state === "checks_failed") : [];
  return (
    <Alert aria-live="polite">
      <AlertTitle>{`${verb} ${count}${state}`}</AlertTitle>
      <AlertDescription>
        <p>{batchTitle(batch)}.</p>
        {last && !completed && <p>The site is updating. This takes about two minutes.</p>}
        {waiting.length > 0 && (
          <p>
            {waiting.length === 1 ? "1 page is" : `${waiting.length} pages are`} approved and waiting for GitHub's automatic
            checks ({waiting.map((r) => r.folder).join(", ")}). Open each page to follow it to live.
          </p>
        )}
        {checksFailed.length > 0 && (
          <p role="alert">
            Approved, but an automatic check failed for {checksFailed.map((r) => r.folder).join(", ")}. A failed check is re-run once by itself; open each page to follow it, or to press Try again once it offers it.
          </p>
        )}
        {viewState === "failed" && view?.url && (
          <a href={view.url} target="_blank" rel="noreferrer" className="underline">Deploy log</a>
        )}
        {(viewState === "promoting" || viewState === "promote_failed") && view?.url && (
          <a href={view.url} target="_blank" rel="noreferrer" className="underline">Promote log</a>
        )}
        {failed.length > 0 && (
          <ul className="mt-2 flex flex-col gap-1" role="alert">
            {failed.map((r) => (
              <li key={r.folder}>
                {r.folder}: {writeMessage(new LandingPagesError(0, { error: r.error }))}
              </li>
            ))}
          </ul>
        )}
      </AlertDescription>
    </Alert>
  );
}

function BatchPanel({
  batch,
  funnelSlug,
  onDecided,
}: {
  batch: Batch;
  funnelSlug: string;
  onDecided: (outcome: Outcome) => void;
}) {
  const me = useMe();
  const refresh = useRefreshLanding();
  const client = useQueryClient();
  const [confirming, setConfirming] = useState<"approve" | "reject" | null>(null);
  const decide = useMutation({
    mutationFn: (decision: "approve" | "reject") =>
      decideBatch(batch.batch_id, decision, Object.fromEntries(batch.items.map((i) => [i.folder, i.head_sha]))),
    onSuccess: (result, decision) => {
      setConfirming(null);
      onDecided({ decision, results: result.data.results });
      if (decision === "reject" || result.data.results.some((r) => !r.ok)) void refresh();
    },
    onError: () => void client.invalidateQueries({ queryKey: batchesKey(funnelSlug) }),
  });

  const first = batch.items[0]?.changes.find((c) => c.action !== "approve");
  return (
    <Alert>
      <AlertTitle>
        ◐ Awaiting Damien · {batchTitle(batch)}
      </AlertTitle>
      <AlertDescription>
        <p className="text-sm">
          By {personName(batch.by)}, {formatDay(batch.at.slice(0, 10))}
          {first?.detail ? ` · ${first.detail}` : ""}
        </p>
        <ul className="my-2 flex flex-col gap-2">
          {batch.items.map((item) => (
            <li key={item.folder} className="flex flex-wrap items-center gap-2">
              <span className="min-w-28 font-medium">{languageName(item.locale)}</span>
              <span className="text-sm">
                {item.changes.filter((c) => c.action !== "approve").map(changeLine).join("; ")} ·{" "}
                <a href={item.pr.url} target="_blank" rel="noreferrer" className="underline">PR #{item.pr.number}</a>
              </span>
              <PreviewLink preview={item.preview} />
            </li>
          ))}
        </ul>
        {me?.can_approve && (
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={() => setConfirming("approve")}>Approve all {batch.items.length}</Button>
            <Button size="sm" variant="outline" className="text-foreground" onClick={() => setConfirming("reject")}>
              Reject all
            </Button>
          </div>
        )}
        {decide.isError && <p className="mt-2 text-sm" role="alert">{writeMessage(decide.error)}</p>}
      </AlertDescription>
      <Dialog open={confirming !== null} onOpenChange={(open) => !open && setConfirming(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirming === "approve" ? `Approve all ${batch.items.length}?` : `Reject all ${batch.items.length}?`}
            </DialogTitle>
            <DialogDescription>
              {confirming === "approve"
                ? batchEffectLine(batch)
                : `Rejecting discards the pending change on every page in this group (${names(batch.items.map((i) => i.locale))}). The live pages stay as they are.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirming(null)}>Cancel</Button>
            <Button
              variant={confirming === "reject" ? "destructive" : "default"}
              disabled={decide.isPending}
              onClick={() => confirming && decide.mutate(confirming)}
            >
              {decide.isPending ? "Working…" : confirming === "approve" ? batchConfirmLabel(batch) : "Reject all"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Alert>
  );
}

/** Retire one version in every language at once; refused wherever it is the only live version. */
export function RetireVariantButton({ funnelSlug, pages }: { funnelSlug: string; pages: PageSummary[] }) {
  const me = useMe();
  const refresh = useRefreshLanding();
  const [open, setOpen] = useState(false);
  const variants = retirableVariants(pages);
  const [variant, setVariant] = useState("");
  const [typed, setTyped] = useState("");
  const [result, setResult] = useState<RetireVariantResult | null>(null);
  const chosen = variants.includes(variant) ? variant : "";
  const plan = chosen ? retireVariantPlan(pages, chosen) : null;
  const confirm = `${funnelSlug}-${chosen}`;
  const retire = useMutation({
    mutationFn: () => retireVariant(funnelSlug, chosen),
    onSuccess: (response) => {
      setResult(response.data);
      void refresh();
    },
  });
  const close = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setVariant("");
      setTyped("");
      setResult(null);
      retire.reset();
    }
  };

  if (!me?.can_edit || variants.length < 2) return null;
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>Retire a version…</Button>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Retire a version in every language</DialogTitle>
            <DialogDescription>
              Takes one split-test version off the site in every language at once. Its visitors go to the other live
              versions and its leads stay in Drip. This needs Damien's approval.
            </DialogDescription>
          </DialogHeader>
          {result ? (
            <div className="flex flex-col gap-2 text-sm" aria-live="polite">
              <p>
                {result.retired.length > 0
                  ? `Retire pending approval in ${names(result.retired.map((r) => r.locale))}. Approve them together from the funnel page.`
                  : "Nothing was retired."}
              </p>
              {result.refused.length > 0 && (
                <p>
                  Kept in {names(result.refused.map((r) => r.locale))}: it is the only live version there, so retiring
                  it would take the page down.
                </p>
              )}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="retire-variant">Version</Label>
                <Select value={chosen} onValueChange={(value) => { setVariant(value); setTyped(""); }}>
                  <SelectTrigger id="retire-variant" className="w-full">
                    <SelectValue placeholder="Choose…" />
                  </SelectTrigger>
                  <SelectContent>
                    {variants.map((v) => (
                      <SelectItem key={v} value={v}>Version {v.toUpperCase()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {plan && (
                <div className="flex flex-col gap-1 text-sm">
                  <p>
                    {plan.retire.length > 0
                      ? `Retires it in ${names(plan.retire)}.`
                      : "It is the only live version in every language it runs in, so nothing can be retired."}
                  </p>
                  {plan.refused.length > 0 && plan.retire.length > 0 && (
                    <p>Keeps it in {names(plan.refused)}: it is the only live version there.</p>
                  )}
                </div>
              )}
              {plan && plan.retire.length > 0 && (
                <>
                  <Label htmlFor="retire-variant-confirm">Type {confirm} to confirm</Label>
                  <Input id="retire-variant-confirm" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={confirm} />
                </>
              )}
              {retire.isError && <p className="text-sm text-ks-ink" role="alert">{writeMessage(retire.error)}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => close(false)}>{result ? "Close" : "Cancel"}</Button>
            {!result && (
              <Button
                variant="destructive"
                disabled={!plan || plan.retire.length === 0 || typed !== confirm || retire.isPending}
                onClick={() => retire.mutate()}
              >
                {retire.isPending ? "Saving…" : "Retire (pending approval)"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Why the Worker kept a language out of a bulk recover, in plain words. */
function recoverRefusal(reason: string): string {
  return reason === "pending_change"
    ? "that page already has a change waiting for approval"
    : reason === "not_retired"
      ? "that page is not retired"
      : `it could not be recovered (${reason})`;
}

/** Recover one retired version in every language it was retired in, as one batch. The mirror of bulk retire. */
export function RecoverVariantButton({ funnelSlug, pages }: { funnelSlug: string; pages: PageSummary[] }) {
  const me = useMe();
  const refresh = useRefreshLanding();
  const [open, setOpen] = useState(false);
  const variants = recoverableVariants(pages);
  const [variant, setVariant] = useState("");
  const [typed, setTyped] = useState("");
  const [result, setResult] = useState<RecoverVariantResult | null>(null);
  const chosen = variants.includes(variant) ? variant : "";
  const plan = chosen ? recoverVariantPlan(pages, chosen) : null;
  const confirm = `${funnelSlug}-${chosen}`;
  const recover = useMutation({
    mutationFn: () => recoverVariant(funnelSlug, chosen),
    onSuccess: (response) => {
      setResult(response.data);
      void refresh();
    },
  });
  const close = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setVariant("");
      setTyped("");
      setResult(null);
      recover.reset();
    }
  };

  if (!me?.can_edit || variants.length === 0) return null;
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)}>Recover a version…</Button>
      <Dialog open={open} onOpenChange={close}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Recover a version in every language</DialogTitle>
            <DialogDescription>
              Puts a retired split-test version back on the site in every language it was retired in, all at once. It
              gets a share of the visitors again. This needs Damien's approval.
            </DialogDescription>
          </DialogHeader>
          {result ? (
            <div className="flex flex-col gap-2 text-sm" aria-live="polite">
              <p>
                {result.recovered.length > 0
                  ? `Recover pending approval in ${names(result.recovered.map((r) => r.locale))}. Approve them together from the funnel page.`
                  : "Nothing was recovered."}
              </p>
              {result.refused.map((r) => (
                <p key={r.folder}>
                  Not recovered in {languageName(r.locale)}: {recoverRefusal(r.reason)}.
                </p>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <Label htmlFor="recover-variant">Version</Label>
                <Select value={chosen} onValueChange={(value) => { setVariant(value); setTyped(""); }}>
                  <SelectTrigger id="recover-variant" className="w-full">
                    <SelectValue placeholder="Choose…" />
                  </SelectTrigger>
                  <SelectContent>
                    {variants.map((v) => (
                      <SelectItem key={v} value={v}>Version {v.toUpperCase()}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {plan && (
                <div className="flex flex-col gap-1 text-sm">
                  <p>
                    {plan.recover.length > 0
                      ? `Brings it back in ${names(plan.recover)}.`
                      : "Every retired page of this version already has a change waiting for approval, so nothing can be recovered now."}
                  </p>
                  {plan.refused.length > 0 && plan.recover.length > 0 && (
                    <p>Leaves out {names(plan.refused)}: a change is already waiting for approval there.</p>
                  )}
                </div>
              )}
              {plan && plan.recover.length > 0 && (
                <>
                  <Label htmlFor="recover-variant-confirm">Type {confirm} to confirm</Label>
                  <Input id="recover-variant-confirm" value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={confirm} />
                </>
              )}
              {recover.isError && <p className="text-sm text-ks-ink" role="alert">{writeMessage(recover.error)}</p>}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => close(false)}>{result ? "Close" : "Cancel"}</Button>
            {!result && (
              <Button
                disabled={!plan || plan.recover.length === 0 || typed !== confirm || recover.isPending}
                onClick={() => recover.mutate()}
              >
                {recover.isPending ? "Saving…" : "Recover (pending approval)"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
